import { toDate, type DateLike } from './parse.js';
import { getResolvedLocale } from './localize.js';

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31536000000],
  ['month', 2592000000],
  ['week', 604800000],
  ['day', 86400000],
  ['hour', 3600000],
  ['minute', 60000],
  ['second', 1000]
];

export function formatRelative(input: DateLike, baseDate: DateLike = new Date(), locale?: string): string {
  const target = toDate(input).getTime();
  const base = toDate(baseDate).getTime();
  const diff = target - base;
  const rtf = new Intl.RelativeTimeFormat(locale ?? getResolvedLocale(), { numeric: 'auto' });
  for (const [unit, msInUnit] of UNITS) {
    if (Math.abs(diff) >= msInUnit || unit === 'second') {
      const value = Math.round(diff / msInUnit);
      return rtf.format(value, unit);
    }
  }
  return rtf.format(0, 'second');
}

export function formatRelativeShort(input: DateLike, baseDate: DateLike = new Date()): string {
  const target = toDate(input).getTime();
  const base = toDate(baseDate).getTime();
  const diff = base - target;
  const abs = Math.abs(diff);
  if (abs < 60_000) return 'now';
  const suffix = diff >= 0 ? '' : '';
  if (abs < 3_600_000) return `${Math.round(abs / 60_000)}m${suffix}`;
  if (abs < 86_400_000) return `${Math.round(abs / 3_600_000)}h${suffix}`;
  if (abs < 604_800_000) return `${Math.round(abs / 86_400_000)}d${suffix}`;
  if (abs < 2_592_000_000) return `${Math.round(abs / 604_800_000)}w${suffix}`;
  if (abs < 31_536_000_000) return `${Math.round(abs / 2_592_000_000)}mo${suffix}`;
  return `${Math.round(abs / 31_536_000_000)}y${suffix}`;
}

export function humanizeDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const parts: string[] = [];
  const days = Math.floor(ms / 86_400_000);
  if (days > 0) parts.push(`${days}d`);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  if (hours > 0) parts.push(`${hours}h`);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  if (minutes > 0) parts.push(`${minutes}m`);
  const seconds = Math.floor((ms % 60_000) / 1000);
  if (seconds > 0 && parts.length < 2) parts.push(`${seconds}s`);
  return parts.join(' ') || '0s';
}
